import { styled } from "@mui/material/styles";
import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, Typography, Button } from "@mui/material";

import RuptureSetGeneralView from "./RuptureSetGeneralView";
import FlipChartControls from "../diagnosticReportCard/FlipChartControls";
import { MetaToolTip } from "../common/MetaToolTip";
import { filterMetaArguments, filteredMetaGT } from "../diagnosticReportCard/diagnosticReportCard.service";
import { SweepArguments, MetaArguments } from "../../views/GeneralTask/generalTask.types";
import { RuptureSetDiagnosticContainerQuery$data } from "../../views/GeneralTask/__generated__/RuptureSetDiagnosticContainerQuery.graphql";
import DisableHotkeyContext from "../../contexts/DisableHotkeyContext";
import { viewPath } from "../../utils/routeUtils";

const Container = styled("div")(() => ({
  flexGrow: 1,
}));

interface RuptureSetDiagnosticReportCardProps {
  ruptureSets: RuptureSetDiagnosticContainerQuery$data;
  sweepArgs?: SweepArguments;
  sweepList?: string[];
}

const RuptureSetDiagnosticReportCard: React.FC<RuptureSetDiagnosticReportCardProps> = ({
  ruptureSets,
  sweepArgs,
  sweepList,
}: RuptureSetDiagnosticReportCardProps) => {
  const [currentImage, setCurrentImage] = useState<number>(0);
  const [filteredMeta, setFilteredMeta] = useState<MetaArguments>([]);
  const { disableHotkey } = useContext(DisableHotkeyContext);

  const solutionList = ruptureSets?.nodes?.result?.edges || [];

  useEffect(() => {
    setCurrentImage(0);
  }, [ruptureSets]);

  useEffect(() => {
    if (solutionList[currentImage]) {
      let metaList: MetaArguments = [];
      if (sweepArgs) {
        metaList = filteredMetaGT(solutionList[currentImage]?.node?.meta, sweepArgs);
      } else if (sweepList) {
        metaList = filterMetaArguments(solutionList[currentImage]?.node?.meta, sweepList);
      }
      setFilteredMeta(metaList);
    }
  }, [currentImage, sweepList, sweepArgs, solutionList]);

  const nextImage = () => {
    if (currentImage < solutionList.length - 1) {
      setCurrentImage(currentImage + 1);
    }
  };

  const prevImage = () => {
    if (currentImage > 0) {
      setCurrentImage(currentImage - 1);
    }
  };

  const hotkeyHandler = (event: KeyboardEvent) => {
    if (disableHotkey === false) {
      if (event.key === ">" || event.key === "." || event.key === "ArrowRight") nextImage();
      if (event.key === "<" || event.key === "," || event.key === "ArrowLeft") prevImage();
    }
  };

  useEffect(() => {
    window.addEventListener("keyup", hotkeyHandler);
    return () => window.removeEventListener("keyup", hotkeyHandler);
  });

  if (!solutionList[currentImage]) {
    return <Typography> There are no valid reports to show. </Typography>;
  }

  return (
    <Container>
      <Card>
        <CardContent>
          <h4>
            {solutionList[currentImage]?.node?.__typename}
            :&nbsp;
            {solutionList[currentImage]?.node?.id || ""}&nbsp;&nbsp;&nbsp;
            <Button component={Link} to={viewPath(solutionList[currentImage]?.node?.id)}>
              more
            </Button>
          </h4>
          <MetaToolTip meta={filteredMeta} />
          <FlipChartControls
            id={solutionList[currentImage]?.node?.id || ""}
            currentImage={currentImage}
            handlePrev={prevImage}
            handleNext={nextImage}
            totalLength={solutionList.length}
          />
          <RuptureSetGeneralView
            id={solutionList[currentImage]?.node?.id || ""}
            filteredMeta={filteredMeta}
            currentImage={currentImage}
            automationTasksLength={solutionList.length}
          />
        </CardContent>
      </Card>
    </Container>
  );
};

export default RuptureSetDiagnosticReportCard;
